/* DatePickerRenderer.js */

"use strict";
const _ = require("lodash");

class DatePickerRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("date-picker-gl"); //TODO Setup the root Element
        if (this._model.disabled) {
            this._rootElem.classList.add("disabled");
        }
        if (this._model.darkTheme) {
            this._rootElem.classList.add("dark");
        }
        if (this._model.theme) {
            this._rootElem.classList.add(this._model.theme);
        }
        if (!this._model.visible) {
            this._rootElem.classList.add("hidden");
        }
        this._rootElem.addEventListener("focus", (this._onFocused = this._onFocused.bind(this)));
        this._rootElem.addEventListener("blur", (this._onBlurred = this._onBlurred.bind(this)));
        this._yearsOffset = 2;
        this._rootElem.component = this._model;
        this._onWheelThrottled = _.throttle(this._onWheel, 150);
        this._rootElem.addEventListener(
            "wheel",
            (this._onWheelThrottled = this._onWheelThrottled.bind(this)),
            {passive: true}
        );
        this._registerListeners();
        this._onDayClickBind = this._onDayClick.bind(this);
        this._onYearClickBind = this._onYearClick.bind(this);
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("CalendarChange", (this._renderCalendar = this._renderCalendar.bind(this)));
        this._model.on("Disabled", (this._onDisabled = this._onDisabled.bind(this)));
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("DarkTheme", (this._onDarkTheme = this._onDarkTheme.bind(this)));
        this._model.on("Theme", (this._onTheme = this._onTheme.bind(this)));
        this._model.on("View", (this.render = this.render.bind(this)));
        this._model.on("Focus", (this._onFocus = this._onFocus.bind(this)));
        this._model.on("Blur", (this._onBlur = this._onBlur.bind(this)));
        this._model.on("SetAttribute", (this._onSetAttribute = this._onSetAttribute.bind(this)));
    }

    _unregisterListeners() {
        this._model.removeListener("Value", this._onValue);
        this._model.removeListener("CalendarChange", this._renderCalendar);
        this._model.removeListener("Disabled", this._onDisabled);
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("DarkTheme", this._onDarkTheme);
        this._model.removeListener("Theme", this._onTheme);
        this._model.removeListener("View", this.render);
        this._model.removeListener("Focus", this._onFocus);
        this._model.removeListener("Blur", this._onBlur);
        this._model.removeListener("SetAttribute", this._onSetAttribute);
    }

    _onFocused(evt) {
        this._model._triggerUserFocus(evt);
    }

    _onBlurred(evt) {
        this._model._triggerUserBlur(evt);
    }

    _onFocus() {
        this._rootElem.focus();
    }

    _onBlur() {
        this._rootElem.blur();
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue) {
        this._setAttribute(attrName, attrValue);
    }

    _onVisible(bool) {
        if (bool) {
            this._rootElem.classList.remove("hidden");
        } else {
            this._rootElem.classList.add("hidden");
        }
    }

    _onValue() {
        this.render();
    }

    _onWheel(evt) {
        if (this._yearsContainerElem) {
            if (Math.sign(evt.deltaY) === 1) {
                this._yearsNext();
            } else {
                this._yearsPrev();
            }
        }
    }

    _onDarkTheme() {
        if (this._model.darkTheme) {
            this._rootElem.classList.add("dark");
            if (this._yearsContainerElem) {
                this._yearsPrevChevronElem.setDarkTheme(true);
                this._yearsNextChevronElem.setDarkTheme(true);
            }
            this._navPrevMonthElem.setDarkTheme(true);
            this._navNextMonthElem.setDarkTheme(true);
        } else {
            this._rootElem.classList.remove("dark");
            if (this._yearsContainerElem) {
                this._yearsPrevChevronElem.setDarkTheme(false);
                this._yearsNextChevronElem.setDarkTheme(false);
            }
            this._navPrevMonthElem.setDarkTheme(false);
            this._navNextMonthElem.setDarkTheme(false);
        }
    }

    _onTheme(newVal, oldVal) {
        if (newVal) {
            this._rootElem.classList.add(newVal);
            if (this._yearsContainerElem) {
                this._yearsPrevChevronElem.setTheme(newVal);
                this._yearsNextChevronElem.setTheme(newVal);
            }
            this._navPrevMonthElem.setTheme(newVal);
            this._navNextMonthElem.setTheme(newVal);
        }
        if (oldVal) {
            this._rootElem.classList.remove(oldVal);
        }
    }

    _onDisabled() {
        if (this._model.disabled) {
            this._rootElem.classList.add("disabled");
            this._disableCalendar();
            this._disableNavBar();
            this._disableYears();
        } else {
            this._rootElem.classList.remove("disabled");
            this._enableCalendar();
            this._enableNavBar();
            this._enableYears();
        }
    }

    _onYearClick(evt) {
        let year = Number.parseInt(evt.target.innerHTML);
        this._model.setValue({year: year}, true, event);
        this._model.setView("days");
    }

    _disableYears() {
        if (this._yearsContainerElem) {
            this._yearsPrevElem.removeEventListener("mouseover", this._onYearsPrevMouseOver);
            this._yearsPrevElem.removeEventListener("mouseleave", this._onYearsPrevMouseLeave);
            this._yearsNextElem.removeEventListener("mouseover", this._onYearsNextMouseOver);
            this._yearsNextElem.removeEventListener("mouseleave", this._onYearsNextMouseLeave);
            _.each(this._yearElements, (yearElem) => {
                yearElem.removeEventListener("click", this._onYearClickBind);
            });
        }
    }

    _enableYears() {
        if (this._yearsContainerElem) {
            this._yearsPrevElem.addEventListener(
                "mouseover",
                (this._onYearsPrevMouseOver = this._onYearsPrevMouseOver.bind(this))
            );
            this._yearsPrevElem.addEventListener(
                "mouseleave",
                (this._onYearsPrevMouseLeave = this._onYearsPrevMouseLeave.bind(this))
            );
            this._yearsNextElem.addEventListener(
                "mouseover",
                (this._onYearsNextMouseOver = this._onYearsNextMouseOver.bind(this))
            );
            this._yearsNextElem.addEventListener(
                "mouseleave",
                (this._onYearsNextMouseLeave = this._onYearsNextMouseLeave.bind(this))
            );
            _.each(this._yearElements, (yearElem) => {
                yearElem.addEventListener("click", this._onYearClickBind);
            });
        }
    }

    _renderYears() {
        if (!this._yearsContainerElem) {
            this._yearsContainerElem = document.createElement("years-container-gl");
            // PAGE PREV
            this._yearsPrevElem = document.createElement("years-prev-gl");
            this._yearsPrevElem.addEventListener(
                "click",
                (this._yearsPrev = this._yearsPrev.bind(this))
            );
            this._yearsPrevChevronElem = document.createElement("base-icon-gl");
            this._yearsPrevChevronElem.dataset.icon = "common.chevron";
            this._yearsPrevChevronElem.classList.add("up", "hidden");
            if (this._model.darkTheme) {
                this._yearsPrevChevronElem.classList.add("dark");
            }
            if (this._model.theme) {
                this._yearsPrevChevronElem.classList.add(this._model.theme);
            }
            this._yearsPrevElem.appendChild(this._yearsPrevChevronElem);
            this._yearsPrevElem.addEventListener(
                "mouseover",
                (this._onYearsPrevMouseOver = this._onYearsPrevMouseOver.bind(this))
            );
            this._yearsPrevElem.addEventListener(
                "mouseleave",
                (this._onYearsPrevMouseLeave = this._onYearsPrevMouseLeave.bind(this))
            );
            this._yearsContainerElem.appendChild(this._yearsPrevElem);
            // LIST
            this._yearsListElem = document.createElement("years-list-gl");
            this._yearsContainerElem.appendChild(this._yearsListElem);
            this._yearElements = [];
            let selectedYear = this._model.value.get("year");
            for (
                let y = selectedYear - this._yearsOffset;
                y <= selectedYear + this._yearsOffset;
                y++
            ) {
                let yearElem = document.createElement("year-gl");
                yearElem.innerHTML = y;
                if (this._model.value.get("year") === y) {
                    yearElem.classList.add("selected");
                }
                yearElem.addEventListener("click", this._onYearClickBind);
                this._yearsListElem.appendChild(yearElem);
                this._yearElements.push(yearElem);
            }
            // PAGE NEXT
            this._yearsNextElem = document.createElement("years-next-gl");
            this._yearsNextElem.addEventListener(
                "click",
                (this._yearsNext = this._yearsNext.bind(this))
            );
            this._yearsNextChevronElem = document.createElement("base-icon-gl");
            this._yearsNextChevronElem.dataset.icon = "common.chevron";
            this._yearsNextChevronElem.classList.add("hidden");
            if (this._model.darkTheme) {
                this._yearsNextChevronElem.classList.add("dark");
            }
            if (this._model.theme) {
                this._yearsNextChevronElem.classList.add(this._model.theme);
            }
            this._yearsNextElem.appendChild(this._yearsNextChevronElem);
            this._yearsNextElem.addEventListener(
                "mouseover",
                (this._onYearsNextMouseOver = this._onYearsNextMouseOver.bind(this))
            );
            this._yearsNextElem.addEventListener(
                "mouseleave",
                (this._onYearsNextMouseLeave = this._onYearsNextMouseLeave.bind(this))
            );
            this._yearsContainerElem.appendChild(this._yearsNextElem);
            this._rootElem.appendChild(this._yearsContainerElem);
        }
    }

    _removeYears() {
        if (this._yearsContainerElem) {
            _.each(this._yearElements, (yearElem) => {
                yearElem.remove();
            });
            this._yearsPrevChevronElem.remove();
            this._yearsPrevChevronElem = undefined;
            this._yearsNextChevronElem.remove();
            this._yearsNextChevronElem = undefined;
            this._yearElements = [];
            this._yearsNextElem.remove();
            this._yearsNextElem = undefined;
            this._yearsPrevElem.remove();
            this._yearsPrevElem = undefined;
            this._yearsListElem.remove();
            this._yearsListElem = undefined;
            this._yearsContainerElem.remove();
            this._yearsContainerElem = undefined;
        }
    }

    _onYearsPrevMouseOver() {
        this._yearsPrevChevronElem.classList.remove("hidden");
    }

    _onYearsPrevMouseLeave() {
        this._yearsPrevChevronElem.classList.add("hidden");
    }

    _onYearsNextMouseOver() {
        this._yearsNextChevronElem.classList.remove("hidden");
    }

    _onYearsNextMouseLeave() {
        this._yearsNextChevronElem.classList.add("hidden");
    }

    _yearsPrev(evt) {
        let year = Number.parseInt(this._yearElements[0].innerHTML) - 1;
        let selectedYear = this._model.value.get("year") - 1;
        this._model.setValue({year: selectedYear}, true, evt);
        for (let i = 0; i <= this._yearElements.length - 1; i++) {
            let yearElem = this._yearElements[i];
            yearElem.innerHTML = year;
            if (this._model.value.get("year") === year) {
                yearElem.classList.add("selected");
            } else {
                yearElem.classList.remove("selected");
            }
            year++;
        }
    }

    _yearsNext(evt) {
        let year = Number.parseInt(this._yearElements[0].innerHTML) + 1;
        let selectedYear = this._model.value.get("year") + 1;
        this._model.setValue({year: selectedYear}, true, evt);
        for (let i = 0; i <= this._yearElements.length - 1; i++) {
            let yearElem = this._yearElements[i];
            yearElem.innerHTML = year;
            if (this._model.value.get("year") === year) {
                yearElem.classList.add("selected");
            } else {
                yearElem.classList.remove("selected");
            }
            year++;
        }
    }

    _renderCalendar() {
        if (!this._calendarContainerElem) {
            this._calendarContainerElem = document.createElement("calendar-container-gl");
            this._rootElem.appendChild(this._calendarContainerElem);
        }
        this._renderNavBar();
        this._renderDayHeaders();
        this._renderDays();
    }

    _removeCalendar() {
        if (this._calendarContainerElem) {
            this._removeNavBar();
            this._removeDayHeaders();
            this._removeDays();
            this._calendarContainerElem.remove();
            this._calendarContainerElem = undefined;
        }
    }

    _enableCalendar() {
        if (this._calendarContainerElem) {
            this._calendarContainerElem.classList.remove("disabled");
            this._enableNavBar();
            this._enableDays();
        }
    }

    _disableCalendar() {
        if (this._calendarContainerElem) {
            this._disableNavBar();
            this._disableDays();
        }
    }

    _renderNavBar() {
        if (!this._calendarNavBarElem) {
            this._calendarNavBarElem = document.createElement("calendar-nav-bar-gl");
            this._calendarContainerElem.appendChild(this._calendarNavBarElem);
            // PREVIOUS BUTTON
            this._navPrevMonthElem = document.createElement("base-icon-gl");
            this._navPrevMonthElem.dataset.icon = "common.chevron";
            this._navPrevMonthElem.classList.add("left");
            this._navPrevMonthElem.classList.add("previous");
            if (this._model.darkTheme) {
                this._navPrevMonthElem.setDarkTheme(true);
            }
            if (this._model.theme) {
                this._navPrevMonthElem.setTheme(this._model.theme);
            }
            this._navPrevMonthElem.addEventListener(
                "click",
                (this._setPreviousMonth = this._setPreviousMonth.bind(this))
            );
            this._calendarNavBarElem.appendChild(this._navPrevMonthElem);
            // NAV LABEL
            this._calendarNavMonthLabelElem = document.createElement("calendar-nav-month-label-gl");
            this._calendarNavMonthLabelElem.innerHTML = this._model.calendar.format("MMM");
            this._calendarNavBarElem.appendChild(this._calendarNavMonthLabelElem);
            this._calendarNavYearLabelElem = document.createElement("calendar-nav-year-label-gl");
            this._calendarNavYearLabelElem.innerHTML = this._model.calendar.format("YYYY");
            this._calendarNavYearLabelElem.addEventListener(
                "click",
                (this._model.showYears = this._model.showYears.bind(this._model))
            );
            this._calendarNavBarElem.appendChild(this._calendarNavYearLabelElem);
            // NEXT BUTTON
            this._navNextMonthElem = document.createElement("base-icon-gl");
            this._navNextMonthElem.dataset.icon = "common.chevron";
            this._navNextMonthElem.classList.add("right");
            this._navNextMonthElem.classList.add("next");
            if (this._model.darkTheme) {
                this._navNextMonthElem.setDarkTheme(true);
            }
            if (this._model.theme) {
                this._navNextMonthElem.setTheme(this._model.theme);
            }
            this._navNextMonthElem.addEventListener(
                "click",
                (this._setNextMonth = this._setNextMonth.bind(this))
            );
            this._calendarNavBarElem.appendChild(this._navNextMonthElem);
        } else {
            // UPDATE LABEL
            this._calendarNavMonthLabelElem.innerHTML = this._model.calendar.format("MMM");
            this._calendarNavYearLabelElem.innerHTML = this._model.calendar.format("YYYY");
        }
    }

    _removeNavBar() {
        if (this._calendarNavBarElem) {
            this._navNextMonthElem.remove();
            this._navNextMonthElem = undefined;
            this._navPrevMonthElem.remove();
            this._navPrevMonthElem = undefined;
            this._calendarNavMonthLabelElem.remove();
            this._calendarNavMonthLabelElem = undefined;
            this._calendarNavYearLabelElem.remove();
            this._calendarNavYearLabelElem = undefined;
            this._calendarNavBarElem.remove();
            this._calendarNavBarElem = undefined;
        }
    }

    _setPreviousMonth() {
        this._model.setPreviousMonth(true);
    }

    _setNextMonth() {
        this._model.setNextMonth(true);
    }

    _disableNavBar() {
        if (this._calendarNavBarElem) {
            this._navNextMonthElem.removeEventListener("click", this._model.setNextMonth);
            this._navPrevMonthElem.removeEventListener("click", this._model.setPreviousMonth);
            this._calendarNavYearLabelElem.removeEventListener("click", this._model.showYears);
            this._navPrevMonthElem.setStateDisabled();
            this._navNextMonthElem.setStateDisabled();
        }
    }

    _enableNavBar() {
        if (this._calendarNavBarElem) {
            this._navNextMonthElem.addEventListener(
                "click",
                (this._model.setNextMonth = this._model.setNextMonth.bind(this._model))
            );
            this._navPrevMonthElem.addEventListener(
                "click",
                (this._model.setPreviousMonth = this._model.setPreviousMonth.bind(this._model))
            );
            this._calendarNavYearLabelElem.addEventListener(
                "click",
                (this._model.showYears = this._model.showYears.bind(this._model))
            );
            this._navPrevMonthElem.clearStates();
            this._navNextMonthElem.clearStates();
        }
    }

    _renderDayHeaders() {
        if (!this._weekHeaderElem) {
            this._weekHeaderElem = document.createElement("calendar-week-header-gl");
            this._weekHeaderElem.innerHTML = `
                <calendar-day-header-gl>S</calendar-day-header-gl>
                <calendar-day-header-gl>M</calendar-day-header-gl>
                <calendar-day-header-gl>T</calendar-day-header-gl>
                <calendar-day-header-gl>W</calendar-day-header-gl>
                <calendar-day-header-gl>T</calendar-day-header-gl>
                <calendar-day-header-gl>F</calendar-day-header-gl>
                <calendar-day-header-gl>S</calendar-day-header-gl>
            `;
            this._calendarContainerElem.appendChild(this._weekHeaderElem);
        }
    }

    _removeDayHeaders() {
        this._weekHeaderElem.remove();
        this._weekHeaderElem = undefined;
    }

    _onDayClick(evt) {
        let day = evt.target._day;
        this._model.setValue(day.moment.toObject(), true, evt);
    }

    _renderDays() {
        if (!this._weeksContainerElem) {
            this._weeksContainerElem = document.createElement("calendar-weeks-container-gl");
            this._calendarContainerElem.appendChild(this._weeksContainerElem);
        } else {
            this._removeDays(false);
        }
        this._weeks = [];
        for (let weekIdx = 0; weekIdx < this._model.calendarWeekDays.length; weekIdx++) {
            let week = this._model.calendarWeekDays[weekIdx];
            let weekElem = document.createElement("calendar-week-gl");
            let dayElements = [];
            for (let i = 0; i < week.length; i++) {
                let day = week[i];
                let dayElem = this._getDayElem(day);
                weekElem.appendChild(dayElem);
                dayElements.push(dayElem);
            }
            this._weeks[weekIdx] = {element: weekElem, dayElements: dayElements};
            this._weeksContainerElem.appendChild(weekElem);
        }
    }

    _getDayElem(day) {
        let dayElem = document.createElement("calendar-day-gl");
        if (day.selectable) {
            dayElem._day = day;
            dayElem.innerHTML = day.moment.format("D");
            let dayTitle = day.moment.format("ddd, MMM D YYYY");
            dayElem.setAttribute("title", dayTitle);
            dayElem.classList.add("selectable");
            if (day.selected) {
                dayElem.classList.add("selected");
            }
            dayElem.addEventListener("click", this._onDayClickBind);
        }
        return dayElem;
    }

    _disableDays() {
        _.each(this._weeks, (week) => {
            _.each(week.dayElements, (dayElem) => {
                dayElem.removeEventListener("click", this._onDayClickBind);
            });
        });
    }

    _enableDays() {
        _.each(this._weeks, (week) => {
            _.each(week.dayElements, (dayElem) => {
                dayElem.addEventListener("click", this._onDayClickBind);
            });
            week.element.classList.remove("disabled");
        });
    }

    _removeDays(removeWeeksContainer = true) {
        _.each(this._weeks, (week) => {
            _.each(week.dayElements, (day) => {
                day.remove();
            });
            week.dayElements = undefined;
            week.element.remove();
        });
        this._weeks = undefined;
        if (removeWeeksContainer) {
            this._weeksContainerElem = undefined;
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        return new Promise((resolve /*,reject*/) => {
            this._removeCalendar();
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            resolve();
        });
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve /*,reject*/) => {
            switch (this._model.view) {
                case "days":
                    this._removeYears();
                    this._renderCalendar();
                    break;
                case "years":
                    this._removeCalendar();
                    this._renderYears();
                    break;
            }
            if (this._model.disabled) {
                this._onDisabled();
            }
            resolve();
        });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        this.render();
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/
    //TODO Private Render Methods here
    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _setAttribute(attrName, attrValue) {
        if (!_.isUndefined(attrValue)) {
            this._rootElem.setAttribute(attrName, attrValue);
        } else {
            this._rootElem.removeAttribute(attrName);
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = DatePickerRenderer;
